import React, { useState, useEffect } from 'react';
import { Sun, Moon, LogOut, Clock } from 'lucide-react';
import { useTheme } from './ThemeContext';
// import { useNavigate } from 'react-router-dom';
import { useAdminInfo } from '../../../pages/Dashboard/DashboarEntreprise/hooksUseretApplication/hookAdmin Info';
import { initiateLogOut } from '../../../../vitrine/services/OauthUtils';

export const Navbar: React.FC = () => {
  const { darkMode, setDarkMode } = useTheme();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { adminInfo, isLoading } = useAdminInfo();
  // const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState<string>('');

  // Mettre à jour l'heure actuelle chaque seconde
  useEffect(() => {
    const updateCurrentTime = () => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const seconds = now.getSeconds().toString().padStart(2, '0');
      setCurrentTime(`${hours}:${minutes}:${seconds}`);
    };

    // Mettre à jour immédiatement
    updateCurrentTime();

    // Mettre à jour chaque seconde
    const intervalId = setInterval(updateCurrentTime, 1000);

    // Nettoyer l'intervalle lors du démontage du composant
    return () => clearInterval(intervalId);
  }, []);

  const getInitials = (name?: string, email?: string) => {
    if (name && name.trim() !== '') {
      const parts = name.split(' ');
      return parts
        .map(part => part.charAt(0).toUpperCase())
        .join('')
        .substring(0, 2);
    }
    
    if (email) {
      return email.split('@')[0].charAt(0).toUpperCase();
    }
    
    return 'U';
  };

  const displayName = () => {
    if (isLoading) return 'Chargement...';
    if (adminInfo?.fullName && adminInfo.fullName.trim() !== '') {
      return adminInfo.fullName;
    }
    return adminInfo?.email || 'Utilisateur';
  };

  return (
    <nav className={`fixed w-full z-30 ${
      darkMode 
     
        ? 'bg-gray-900 text-white border-gray-800' 
        : 'bg-white text-gray-800 border-red-200'
    } border-b`}>
      <div className="px-4 h-16 flex justify-between items-center">
        {/* Logo et titre */}
        <div className="flex items-center space-x-4">
          {/* Monogramme de l'entreprise */}
          <div className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg ${
            darkMode 
              ? 'bg-gradient-to-r from-gray-500 to-red-300' 
              : 'bg-gradient-to-r from-gray-800 to-gray-600'
          }`}>
            <span className="text-white font-bold text-sm">
              {adminInfo?.companyName?.charAt(0) || 'E'}
            </span>
          </div>
          
          {/* Informations entreprise */}
          <div className="flex flex-col">
            <span className="text-sm font-semibold">
              {adminInfo?.companyName || 'Entreprise'}
            </span>
            <span className={`text-xs ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              ID: {localStorage.getItem('company') || 'N/A'}
            </span>
          </div>
        </div>

        {/* Heure actuelle */}
        <div className={`flex items-center ${
          darkMode ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-800'
        } rounded-lg px-3 py-2`}>
          <Clock className="h-4 w-4 mr-2" />
          <span className="text-sm font-medium">{currentTime}</span>
        </div>

        {/* Nom/Email utilisateur */}
        <div className={`hidden md:flex items-center ${
          darkMode ? 'bg-gray-800' : 'bg-gray-100'
        } rounded-full px-4 py-2 mx-4`}>
          <span className="text-sm font-medium truncate max-w-xs">
            {displayName()}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`p-2 rounded-lg ${
              darkMode 
                ? 'hover:bg-gray-800' 
                : 'hover:bg-gray-100'
            }`}
            aria-label="Toggle dark mode"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          {/* Avatar utilisateur */}
          <div className="relative">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              darkMode 
                ? 'bg-indigo-700 text-white' 
                : 'bg-indigo-100 text-indigo-700'
            } font-semibold cursor-pointer`}>
              {getInitials(adminInfo?.fullName, adminInfo?.email)}
            </div>
          </div>

          <button 
            onClick={() => setShowLogoutModal(true)}
            className={`p-2 rounded-lg ${
              darkMode 
                ? 'hover:bg-gray-800 text-red-400' 
                : 'hover:bg-gray-100 text-red-600'
            }`}
            aria-label="Logout"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Modal de confirmation de déconnexion */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm"></div>
          <div className={`relative max-w-md w-full mx-4 rounded-lg shadow-lg p-6 ${
            darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
          }`}>
            <div className="text-center">
              <div className={`mb-4 flex justify-center ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold mb-4">Confirmation de déconnexion</h2>
              <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Êtes-vous sûr de vouloir vous déconnecter ?
              </p>
              <div className="flex space-x-3 justify-center">
                <button 
                  onClick={() => setShowLogoutModal(false)}
                  className={`px-4 py-2 rounded-md font-medium ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Annuler
                </button>
                <button 
                  onClick={initiateLogOut}
                  className="px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700"
                >
                  Se déconnecter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};